import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/solid";

interface BreadcrumbItem {
  path: string;
  label: string;
}

const pathMap: Record<string, string> = {
  "": "My Projects",
  agency_list: "My Projects",
  // network_list: "Network List",
  property_details: "Projects Details",

  customers: "My Customers",
  add_customer: "Add Customer",
  client_details: "Customer Details",
  add_shortlist: "Select Projects",

  earnings: "My Earnings",
  vr_tour_list: "My Virtual Tours",
  profile: "My Profile",
  join_vr_tour: "Join VR Tour",
  properties_shortlist: "My Projects",
  my_agent: "My Agent",
};

const Breadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Check if current path is home page
  const isHomePage = location.pathname === "/";
  // Don't render breadcrumbs on home page
  if (isHomePage) {
    return <nav className="flex items-center text-sm text-gray-600 mb-2"></nav>;
    // return null;
  }

  // Get breadcrumbs from state or generate based on referrer
  const stateBreadcrumbs = location.state?.breadcrumbs as
    | BreadcrumbItem[]
    | undefined;
  const referrer = location.state?.referrer as string | undefined;

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (stateBreadcrumbs) return stateBreadcrumbs;

    const pathnames = location.pathname.split("/").filter(Boolean);
    const crumbs: BreadcrumbItem[] = [{ path: "/", label: "Home" }];

    if (referrer) {
      const referrerLabel =
        pathMap[referrer.split("/").filter(Boolean)[0]] || referrer;
      crumbs.push({ path: referrer, label: referrerLabel });
    }

    if (pathnames.length > 0) {
      const currentPage = pathnames[pathnames.length - 1];
      crumbs.push({
        path: location.pathname,
        label:
          pathMap[currentPage] ||
          currentPage
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" "),
      });
    }

    return crumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <nav className="flex flex-wrap flex-grow items-center text-sm text-gray-600 mb-2 px-2 lg:px-3 gap-1">
      {breadcrumbs.map((crumb, index) => (
        <div key={index} className="flex items-center">
          {index > 0 && (
            <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 mx-1 text-gray-400 shrink-0" />
          )}
          {index === breadcrumbs.length - 1 ? (
            <span className="text-white font-medium whitespace-nowrap text-xs sm:text-sm">
              {crumb.label}
            </span>
          ) : (
            <Link
              to={crumb.path}
              className="text-[#889DB2] hover:underline whitespace-nowrap text-xs sm:text-sm"
              onClick={(e) => {
                e.preventDefault();
                navigate(crumb.path, {
                  state: {
                    breadcrumbs: breadcrumbs.slice(0, index + 1),
                    referrer: referrer,
                  },
                });
              }}
            >
              {crumb.label}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
};

export default Breadcrumbs;
